import React from "react";
import PropTypes from "prop-types";
import { Box } from "@material-ui/core";
import { BRAND } from "src/theme/brandTokens";
import { formatearFecha } from "src/utils/formatTime";

// Texto mega y CTA por estado terminal. El porcentaje se inyecta en
// `mostrarPorcentaje` cuando aplica (ACEPTADA siempre 100%; PARCIALMENTE
// solo si el caller pasa el porcentaje calculado).
const CONFIG = {
  ACEPTADA: {
    titulo: "ACEPTADA",
    mostrarPorcentaje: true,
    subtitulo: "Los servicios aceptados ya fueron activados en tu cuenta.",
  },
  RECHAZADA: {
    titulo: "RECHAZADA",
    mostrarPorcentaje: false,
    subtitulo: "Si deseas reconsiderarla, contacta a tu vendedor.",
  },
  PARCIALMENTE_ACEPTADA: {
    titulo: "PARCIALMENTE ACEPTADA",
    mostrarPorcentaje: true,
    subtitulo:
      "Los servicios aceptados están activos. Tu vendedor puede contactarte por los rechazados.",
  },
  CADUCADA: {
    titulo: "CADUCADA",
    mostrarPorcentaje: false,
    subtitulo: "Contacta a tu vendedor para reactivarla si aún te interesa.",
  },
};

const TerminalStateBanner = ({ estado, fecha, porcentajeAceptado }) => {
  const config = CONFIG[estado];
  if (!config) return null;

  // ACEPTADA implica 100% por definición; PARCIALMENTE depende del cálculo
  // del caller. Si `porcentajeAceptado` no llega para PARCIALMENTE, se omite
  // el sufijo y queda solo el texto del estado.
  let porcentajeMostrado = null;
  if (config.mostrarPorcentaje) {
    if (estado === "ACEPTADA") porcentajeMostrado = 100;
    else if (porcentajeAceptado != null)
      porcentajeMostrado = porcentajeAceptado;
  }

  const fechaFormateada = fecha
    ? formatearFecha(fecha, "DD MMM YYYY").toUpperCase()
    : null;

  return (
    <Box
      component="section"
      sx={{
        bgcolor: BRAND.color.inkSoft,
        color: BRAND.color.paper,
        fontFamily: BRAND.font.family.base,
        width: "100%",
      }}
    >
      <Box
        sx={{
          maxWidth: 1280,
          mx: "auto",
          px: { xs: 3, md: 5 },
          py: { xs: 5, md: 7 },
          display: "flex",
          flexDirection: "column",
          gap: { xs: 2, md: 3 },
        }}
      >
        <Box
          sx={{
            fontSize: BRAND.font.size.eyebrow,
            color: BRAND.color.primary,
            textTransform: "uppercase",
            letterSpacing: BRAND.font.letterSpacing.widest,
            fontWeight: BRAND.font.weight.medium,
          }}
        >
          Estado de la propuesta
        </Box>

        <Box
          component="h2"
          sx={{
            m: 0,
            fontFamily: BRAND.font.family.display,
            fontSize: { xs: "32pt", md: BRAND.font.size.h1 },
            fontWeight: BRAND.font.weight.black,
            color: BRAND.color.paper,
            lineHeight: BRAND.font.lineHeight.tight,
            letterSpacing: BRAND.font.letterSpacing.tight,
          }}
        >
          {config.titulo}
          {porcentajeMostrado != null && (
            <Box component="span" sx={{ color: BRAND.color.primary }}>
              {" · "}
              {porcentajeMostrado}%
            </Box>
          )}
        </Box>

        <Box
          sx={{
            fontSize: BRAND.font.size.bodyLarge,
            color: BRAND.color.paper,
            opacity: 0.85,
            lineHeight: BRAND.font.lineHeight.body,
            maxWidth: 720,
          }}
        >
          {config.subtitulo}
        </Box>

        {fechaFormateada && (
          <Box
            sx={{
              borderTop: `1px solid ${BRAND.color.dividerDark}`,
              pt: { xs: 2, md: 3 },
              mt: { xs: 1, md: 2 },
              fontSize: BRAND.font.size.eyebrow,
              color: BRAND.color.grey50,
              textTransform: "uppercase",
              letterSpacing: BRAND.font.letterSpacing.widest,
              fontWeight: BRAND.font.weight.medium,
            }}
          >
            Registrada el {fechaFormateada}
          </Box>
        )}
      </Box>
    </Box>
  );
};

TerminalStateBanner.propTypes = {
  estado: PropTypes.oneOf([
    "ACEPTADA",
    "RECHAZADA",
    "PARCIALMENTE_ACEPTADA",
    "CADUCADA",
  ]).isRequired,
  fecha: PropTypes.string,
  porcentajeAceptado: PropTypes.number,
};

TerminalStateBanner.defaultProps = {
  fecha: null,
  porcentajeAceptado: null,
};

export default React.memo(TerminalStateBanner);
